import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import { getArtists } from '../../store/actions/artist/artist.action';
import { getInstruments } from '../../store/actions/instrument/instrument.action';
import { getAwsKey } from '../../store/actions/piece-video/piece-video.action';
import { createMember, getMemberByID, editMember } from '../../store/actions/member/member.action';

import CreateMember from '../../components/Member/CreateMember';
import PageLoader from '../../components/Common/PageLoader';

const CreatePiecesContainer = () => {
  // Router hooks
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const isCreating = useSelector((state) => state.pieceVideo.isCreating);
  const artists = useSelector((state) => state.artist.list);
  const instruments = useSelector((state) => state.instrument.list);
  const categories = useSelector((state) => state.auth.categories);

  // Local state
  const [startRender, setStartRender] = useState(false);
  const [accessMode, setAccessMode] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [memberToBeEdit, setMemberToBeEdit] = useState(null);
  const [mode, setMode] = useState('CREATE');

  // Set access based on categories
  const setAccess = useCallback(() => {
    try {
      const memberCategory = categories.find((category) => category.link === 'members');
      if (memberCategory) {
        setAccessMode(memberCategory.mode);
        setAccessDenied(false);
      } else {
        throw new Error('Member category not found');
      }
    } catch (err) {
      setAccessDenied(true);
    }
  }, [categories]);

  // Get member for edit mode
  const getMember = useCallback(async (editMode, memberId) => {
    try {
      const member = await dispatch(getMemberByID(memberId));
      setMemberToBeEdit(member);
      setMode(editMode);
      setStartRender(true);
    } catch (error) {
      navigate('/members');
    }
  }, [dispatch, navigate]);

  // Get members attributes
  const getMembersAttribute = useCallback(async (editMode, memberId) => {
    try {
      await Promise.all([
        dispatch(getArtists()),
        dispatch(getInstruments())
      ]);

      if (editMode === 'EDIT') {
        await getMember(editMode, memberId);
      } else {
        setStartRender(true);
      }
    } catch (error) {
      navigate('/members');
    }
  }, [dispatch, navigate, getMember]);

  // Action handlers
  const handleCreateMember = useCallback((memberData) => {
    return dispatch(createMember(memberData));
  }, [dispatch]);

  const handleEditMember = useCallback((memberData) => {
    return dispatch(editMember(memberData));
  }, [dispatch]);

  const handleGetAwsKey = useCallback((data) => {
    return dispatch(getAwsKey(data));
  }, [dispatch]);

  // Initialize on mount
  useEffect(() => {
    const editMode = params && params.id ? 'EDIT' : 'CREATE';
    setMode(editMode);
    getMembersAttribute(editMode, params.id);
    setAccess();
  }, [params.id, getMembersAttribute, setAccess]);

  // Update access when categories change
  useEffect(() => {
    setAccess();
  }, [categories, setAccess]);

  // Loading state
  if (!startRender) {
    return <PageLoader />;
  }

  // Access denied state
  if (accessDenied) {
    return <div>This category is not accessible to the logged in user.</div>;
  }

  // Prepare member info object
  const memberInfo = {
    artists,
    instruments,
    isCreating,
    getAwsKey: handleGetAwsKey,
    editMember: handleEditMember,
    createMember: handleCreateMember,
    initialValues: memberToBeEdit,
    mode: mode,
  };

  return (
    <section>
      <CreateMember accessMode={accessMode} memberInfo={memberInfo} />
    </section>
  );
};

export default CreatePiecesContainer;
